import styled from '@emotion/styled'
import { useEffect, useMemo, useState } from 'react'
import { fetchScrollOverMedia } from '../../data/prismic'
import { ScrollOverMediaDocument } from '../../../types.generated'
import ScrollOverMediaBackground from './ScrollOverMediaBackground'
import ScrollOverMediaForegrounds from './ScrollOverMediaForeground'
import {
  VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP,
  VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE,
} from '../../theme/theme.tsx'
import { BREAKPOINT_MOBILE } from '../../util/breakpoints.ts'
import { RootCover } from './foregrounds/Covers.tsx'
import { VIEWPORT_HEIGHT } from '../../util/viewportHeight.ts'
import { useInView } from 'react-intersection-observer'

export interface ScrollOverMediaProps {
  id: string
  lang: string
  sectionIndex: number
  disableBottomMargin: boolean
  isAudiostory: boolean
}

// step scroll progress in a map of <stepId, scrollProgressPercentage>
export type StepsScrollProgress = Record<number, number>

export default function ScrollOverMedia(props: ScrollOverMediaProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [stepsScrollProgress, setStepsScrollProgress] =
    useState<StepsScrollProgress>({})
  const [scrollOverMediaDoc, setScrollOverMediaDoc] =
    useState<ScrollOverMediaDocument>()
  const [isLoading, setIsLoading] = useState(true)

  const { ref, inView } = useInView({
    threshold: 0.01,
  })

  useEffect(() => {
    if (inView && import.meta.env.VITE_ANALYTICS_TRACK_SECTION_VIEW) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).dataLayer = (window as any).dataLayer || []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).dataLayer.push({
        event: 'section_view',
        section_name: props.sectionIndex.toString(),
      })
    }
  }, [inView, props.sectionIndex])

  useEffect(() => {
    const fetch = async () => {
      const scrollOverMedia = await fetchScrollOverMedia(props.id, props.lang)
      if (scrollOverMedia) {
        setScrollOverMediaDoc(scrollOverMedia)
        setIsLoading(false)
      } else {
        console.error(
          `Scroll over media with id ${props.id} and lang ${props.lang} was not found!`
        )
        setIsLoading(false)
      }
    }
    fetch()
  }, [props.id, props.lang])

  const foregroundsMemo = useMemo(
    () =>
      scrollOverMediaDoc ? (
        <ScrollOverMediaForegrounds
          doc={scrollOverMediaDoc}
          setActiveStep={setActiveStep}
          setStepsScrollProgress={setStepsScrollProgress}
        />
      ) : null,
    [scrollOverMediaDoc]
  )

  const rootCoverMemo = useMemo(
    () =>
      scrollOverMediaDoc ? (
        <RootCover covers={scrollOverMediaDoc.data.covers} />
      ) : null,
    [scrollOverMediaDoc]
  )

  return (
    <>
      {isLoading && (
        // LoadingContainer does not display anything, but takes up space to avoid cumulative layout shifts
        <LoadingContainer role="status" aria-live="polite" />
      )}
      {scrollOverMediaDoc && (
        <ScrollOverMediaContainer
          ref={ref}
          disableMargin={props.disableBottomMargin}
          data-cy="scrollOverMediaContainer"
        >
          <ScrollOverMediaBackground
            doc={scrollOverMediaDoc}
            activeStep={activeStep}
            stepsScrollProgress={stepsScrollProgress}
            sectionIndex={props.sectionIndex}
            lang={props.lang}
            isAudiostory={props.isAudiostory}
          />

          {rootCoverMemo}
          {foregroundsMemo}
        </ScrollOverMediaContainer>
      )}
    </>
  )
}

const ScrollOverMediaContainer = styled.div<{ disableMargin: boolean }>`
  position: relative;

  margin-bottom: ${({ disableMargin }) =>
    disableMargin ? 0 : `${VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP}`};
  @media ${BREAKPOINT_MOBILE} {
    margin-bottom: ${({ disableMargin }) =>
    disableMargin ? 0 : `${VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE}`};
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${VIEWPORT_HEIGHT};
  width: 100%;
`
